import { signal } from '@preact/signals-react';
import { ControllerProtocol, Injectable, Scopes } from '@visiba/instantiation';
import { RouterService } from '@cortex-shim/router.service';
import { Logger } from '@visiba/logger';
import { UniversalConfig } from '@application/universal_config.service';
import { UnitsApiService } from '@api_patient/units_api.service';
import { RateApiService } from '@api_patient/rate_api.service';
import { BasketService } from './basket.service';
import { UnitService } from '@application/unit.service';
import { CreateNewMessageSettingsService } from './create_new_message_settings.service';
import { RedRobinApiService } from '@api_patient/red_robin_api.service';
import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import type { ClientConditionApiModel, RateApiModel } from '@api_patient/generated/clientv2/models';
import type { AsyncSignalValue } from '@component/async.component';
import type { Basket } from './basket.service';
import type { CreateNewMessageSettings } from './create_new_message_settings.service';

@Injectable({
	scope: Scopes.Scoped,
})
export class CreateNewMessageController implements ControllerProtocol {
	public flowData = signal<AsyncSignalValue<CreateNewMessageFlowData>>({
		value: null,
		flowState: 'pending',
	});

	public screenState = signal<'idle' | 'busy'>('idle');
	public flowSettings = signal<AsyncSignalValue<CreateNewMessageSettings>>({
		value: null,
		flowState: 'pending',
	});

	private receptionId: number;
	private readonly logger = new Logger('CreateNewMessageController');
	private readonly routeBase: string;

	constructor(
		private readonly routerService: RouterService,
		private readonly universalConfig: UniversalConfig,
		private readonly unitsApiService: UnitsApiService,
		private readonly rateApiService: RateApiService,
		private readonly basketService: BasketService,
		private readonly unitService: UnitService,
		private readonly createNewMessageSettingsService: CreateNewMessageSettingsService,
		private readonly redRobinApiService: RedRobinApiService,
		private readonly analyticService: AnalyticService,
	) {
		const unit = this.unitService.getUnitNodeInTree(this.universalConfig.selectedUnitId);
		this.routeBase = unit.routeWithoutLicenceholder;

		const receptionId = unit.receptionId;
		this.receptionId = receptionId;

		this.basketService.receptionId = receptionId;
	}

	async whenMount() {
		this.logger.logInformation('Mounting CreateNewMessageController');

		this.flowData.value = {
			value: null,
			flowState: 'pending',
		};

		const [priceResult, startMessageResult, reasonForVisitsResult] = await Promise.all([
			this.fetchPrice(),
			this.fetchTexts(),
			this.fetchReasonForVisits(),
		]);

		if (priceResult.err || startMessageResult.err || reasonForVisitsResult.err) {
			this.flowData.value = {
				value: null,
				flowState: 'error',
			};

			return;
		}

		this.flowData.value = {
			value: {
				price: priceResult.ok.PaymentWorkflow !== 'NoPayment' ? priceResult.ok.Price.Formatted : null,
				requirePayment: priceResult.ok.PaymentWorkflow === 'ByUserBeforeEntering',
				screenStart: startMessageResult.ok,
				reasonForVisits: reasonForVisitsResult.ok,
			},
			flowState: 'accessible',
		};
	}

	public async setupNewBasket(): AsyncResult<void> {
		this.logger.logInformation('Setup basket');

		// Delete existing basket
		await this.deleteBasket();
		await this.basketService.restore(this.receptionId ?? -1);

		const basketId = this.basketService.basketId;
		if (!basketId) return Err(new Error('No basket found...'));

		const queryParams = new URLSearchParams(window.location.search);

		const redRobinSessionId = queryParams.get('redRobinSessionId');
		let asAgentFor = null;

		if (redRobinSessionId) {
			const redRobinResult = await this.redRobinApiService.getRepresentativeNin(redRobinSessionId);

			if (redRobinResult.err) {
				this.logger.logError('Failed to get RedRobin session');

				return Err(redRobinResult.err);
			}

			asAgentFor = redRobinResult.ok.data.RepresentativeNin;
		} else {
			asAgentFor = queryParams.get('nin') ?? null;
		}

		const flowSettings = {
			redRobinSessionId: redRobinSessionId,
			asAgentFor,
			predefinedDescriptionOfMatter: queryParams.get('dom') ?? null,
			predefinedReasonForVisit:
				queryParams
					.get('rfv')
					?.split(',')
					.map((id) => parseInt(id, 10)) ?? null,
			attachFiles: queryParams.get('file') !== 'false',
		};

		this.createNewMessageSettingsService.setSettings({
			...flowSettings,
		});

		return Ok();
	}

	public getFlowSettings(): CreateNewMessageSettings {
		const flowSettings = this.flowSettings.peek();

		if (flowSettings.flowState !== 'accessible') {
			throw new Error('Flow settings not accessible yet.');
		}

		return flowSettings.value;
	}

	public getFlowData(): CreateNewMessageFlowData {
		const flowData = this.flowData.peek();
		if (flowData.flowState !== 'accessible') {
			throw new Error('Flow data not accessible yet.');
		}

		return flowData.value;
	}

	public getFilteredReasonForVisits(): ClientConditionApiModel[] {
		let filteredReasonForVisits: ClientConditionApiModel[] = [];

		const flowSettings = this.getFlowSettings();
		const flowData = this.getFlowData();

		if (flowSettings.predefinedReasonForVisit) {
			if (flowSettings.predefinedReasonForVisit.length > 1) {
				filteredReasonForVisits =
					flowData.reasonForVisits.filter((rfv) => flowSettings.predefinedReasonForVisit?.includes(rfv.Id)) ?? [];
			}
		} else {
			filteredReasonForVisits = flowData.reasonForVisits ?? [];
		}

		return filteredReasonForVisits;
	}

	public async loadSettings() {
		this.logger.logInformation('Load settings');

		if (!this.basketService.basket) {
			await this.basketService.restore(this.receptionId ?? -1);
		}

		const basketId = this.basketService.basketId;
		if (!basketId) {
			throw new Error('WE NEED A BASKET!!');
		}

		const settings = this.createNewMessageSettingsService.getSettings();

		this.flowSettings.value = {
			value: settings,
			flowState: 'accessible',
		};

		const analyticsSettings = [];

		if (settings.asAgentFor) analyticsSettings.push('asAgentFor');
		if (settings.redRobinSessionId) analyticsSettings.push('redRobinSessionId');
		if (settings.predefinedDescriptionOfMatter) analyticsSettings.push('predefinedDescriptionOfMatter');
		if (settings.predefinedReasonForVisit) analyticsSettings.push('predefinedReasonForVisit');
		if (settings.attachFiles === false) analyticsSettings.push('hideAttachFiles');

		if (analyticsSettings.length) {
			this.analyticService.trackEvent(
				AnalyticCategories.CreateNewMessage,
				CreateNewMessageAnalyticActions.UsingSettings,
				analyticsSettings.join(', '),
			);
		}
	}

	public getCurrentBasket(): Nullable<Basket> {
		return this.basketService.get();
	}

	public getSelectedReasonForVisit(): Nullable<ClientConditionApiModel> {
		return this.getFlowData().reasonForVisits.find((rfv) => rfv.Id === this.basketService.reasonForVisitId) ?? null;
	}

	public async deleteBasket(): Promise<void> {
		const basketDeleteResponse = await this.basketService.delete();
		if (basketDeleteResponse.err) {
			this.logger.logError('User tried to delete basket: {message}', basketDeleteResponse.err.message);
		}

		this.createNewMessageSettingsService.clearSettings();
	}

	public goToScreen(
		screen: CreateNewMessageFlowScreen,
		options: { replace?: boolean; formId?: number; formPage?: number; conversationId?: number } = {},
	): void {
		let path = '';
		let analyticActionName = screen;

		switch (screen) {
			case 'start':
				path = 'start';
				break;
			case 'reason-for-visit':
				path = 'reason-for-visit';
				break;
			case 'forms':
				path = 'forms';

				if (options.formId) {
					path += `/${options.formId}`;

					analyticActionName += ' - formId: ${options.formId}';

					if (options.formPage) {
						path += `/${options.formPage}`;

						analyticActionName += ' page: ${options.formPage}';
					}
				}

				break;
			case 'conclusion':
				path = 'conclusion';
				break;
			case 'done':
				path = 'done';

				if (options.conversationId) {
					path += `/${options.conversationId}`;
				}

				break;
			case 'error':
				path = 'error';
				break;
		}

		this.analyticService.trackEvent(
			AnalyticCategories.CreateNewMessage,
			CreateNewMessageAnalyticActions.GoToScreen,
			analyticActionName,
		);

		const url = [this.routeBase, 'message', path].filter(Boolean);

		this.routerService.navigateByUrl(`/${url.join('/')}`, { replace: options.replace });
	}

	public goToReceptionHome() {
		this.routerService.navigateByUrl(`/${this.routeBase}`);
	}

	private async fetchPrice(): AsyncResult<RateApiModel> {
		const response = await this.rateApiService.getMessagingConversationPrice(this.receptionId ?? -1);
		if (response.err) return Err(response.err);

		return Ok(response.ok.data);
	}

	private async fetchTexts(): AsyncResult<ScreenStartData> {
		this.logger.logInformation('Get start screen texts');
		const messageResult = await this.unitsApiService.getTexts(this.universalConfig.selectedUnitId, [
			'Messaging_Intro_Title',
			'Messaging_Intro_Message',
		]);
		if (messageResult.err) {
			this.logger.logError(
				'User tried to get start tests: {message} {statusCode}',
				messageResult.err.message,
				messageResult.err.statusCode,
			);

			return Err(messageResult.err);
		}

		return Ok({
			title: messageResult.ok.data.Messaging_Intro_Title,
			description: messageResult.ok.data.Messaging_Intro_Message,
		});
	}

	private async fetchReasonForVisits(): AsyncResult<ClientConditionApiModel[]> {
		this.logger.logInformation('Get reason for visits');

		const unitConfigResult = await this.unitsApiService.getUnitConfig(this.universalConfig.selectedUnitId);
		if (unitConfigResult.err) return Err(unitConfigResult.err);

		if (unitConfigResult.ok.data.Offer?.HasConditionWorkflow === false) {
			return Ok([]);
		}

		const reasonForVisitsResult = await this.unitsApiService.getConditions(this.universalConfig.selectedUnitId);
		if (reasonForVisitsResult.err) {
			this.logger.logError(
				'User tried to get reason for visits: {message} {statusCode}',
				reasonForVisitsResult.err.message,
				reasonForVisitsResult.err.statusCode,
			);

			return Err(reasonForVisitsResult.err);
		}

		return Ok(reasonForVisitsResult.ok.data);
	}
}

export enum CreateNewMessageAnalyticActions {
	UsingSettings = 'Using settings',
	GoToScreen = 'Go-to-screen',
	NotAllowedToBeHere = 'Not allowed to be here',
	AutoSetReasonForVisit = 'Automatically set reason for visit',
	SelectReasonForVisit = 'Select reason for visit',
	NoReasonForVisits = 'No reason for visits',
	NoForms = 'No forms',
	NumberOfForms = 'Number of forms',
	LoadForm = 'Load form',
}

interface ScreenStartData {
	title: Nullable<string>;
	description: Nullable<string>;
}

export interface CreateNewMessageFlowData {
	price: Nullable<string>;
	requirePayment: boolean;
	reasonForVisits: ClientConditionApiModel[];
	screenStart: ScreenStartData;
}

type CreateNewMessageFlowScreen = 'reason-for-visit' | 'forms' | 'conclusion' | 'error' | 'start' | 'done';
